import { Config } from 'modules/config/state/config-types'

/**
 * Address Questions Utilities
 *
 * Utilities for handling address questions logic in the single step flow.
 */

// Address-related question tags
export const ADDRESS_TAGS = [
  'address',
  'address2',
  'city',
  'state',
  'postal_code',
  'country',
] as const

/**
 * Checks if there are any required address questions in the config.
 * This is used to determine whether to show DonorInfo or use address questions instead.
 *
 * @param config - The application config containing contact questions
 * @returns true if there are required address questions, false otherwise
 */
export function hasRequiredAddressQuestions(config: Config): boolean {
  return config.questions.contact.some(
    (question) =>
      question.is_required && question.tag && ADDRESS_TAGS.includes(question.tag as any),
  )
}

/**
 * Gets all address questions (both required and optional) from the config.
 *
 * @param config - The application config containing contact questions
 * @returns Array of address questions
 */
export function getAddressQuestions(config: Config) {
  return config.questions.contact.filter(
    (question) => question.tag && ADDRESS_TAGS.includes(question.tag as any),
  )
}

/**
 * Gets only required address questions from the config.
 *
 * @param config - The application config containing contact questions
 * @returns Array of required address questions
 */
export function getRequiredAddressQuestions(config: Config) {
  return config.questions.contact.filter(
    (question) =>
      question.is_required && question.tag && ADDRESS_TAGS.includes(question.tag as any),
  )
}

/**
 * Gets only optional address questions from the config.
 *
 * @param config - The application config containing contact questions
 * @returns Array of optional address questions
 */
export function getOptionalAddressQuestions(config: Config) {
  return config.questions.contact.filter(
    (question) =>
      !question.is_required && question.tag && ADDRESS_TAGS.includes(question.tag as any),
  )
}
